<template>
  <CustomDashboard
    :user-id="userId"
    :dashboard="dashboard"
    :dashboard-owners="dashboardOwners"
    :export-data="exportData"
    @handleSelectionChange="handleSelectionChange"
    @applyFilters="() => loadUnitEconomicsData()"
  />
</template>

<script>
import axios from 'axios'
import CustomDashboard from '@/components/CustomDashboard'
import _ from 'lodash'
import { getData, createInListString, createHolidayQuery, createWeatherQuery } from '@/helpers/custom-dashboard'

const DEFAULT_AGGREGATIONS = ['All Regions', 'Megaregion']

export default {
  components: {
    CustomDashboard
  },

  props: {
    userId: Number
  },

  data () {
    return {
      context: 'dashboards',
      dashboard: DASHBOARD,
      queryLimit: 100000, // Limit for the number of results returned.
      dashboardId: 58,
      dashboardOwners: '',
      initialLoad: true,
      initialDataLoaded: false,
      dashboardOpenLogged: false,
      backfillingRouteQueryParams: false,
      dashboardLoadStart: 0,
      dashboardLoadEnd: 0
    }
  },

  computed: {
    query () {
      let query = `SELECT *, AVG(GROSS_REVENUE) over(partition by REGION) as avg_gross_revenue FROM (
        SELECT *,
        (PAYMENT_FEES + INSURANCE_FEES + NETWORK_FEES) AS PAYMENT_INSURANCE_NETWORK_FEES, 
        (PAYMENT_FEES_PCT_GROSS + INSURANCE_FEES_PCT_GROSS + NETWORK_FEES_PCT_GROSS) AS PAYMENT_INSURANCE_NETWORK_FEES_PCT_GROSS 
        FROM ${this.dashboard.dataSource} WHERE `
      this.dashboard.widgets.forEach(widget => {
        if (widget.metadata.customField || widget.metadata.type === 'table') {
          return
        }

        if (['checkbox', 'select-multiple'].includes(widget.metadata.type)) {
          query += `${widget.metadata.name} IN (${createInListString(widget.value)}) AND `
        } else if (['select', 'input'].includes(widget.metadata.type)) {
          query += `${widget.metadata.name} = '${widget.value}' AND `
        }
      })
      query += `week between dateadd(week, -${this.dashboard.widgets[2].value - 1}, date_trunc(week, current_date)) and date_trunc(week, current_date))`
      query += `
        ORDER BY case when AGGREGATION = 'All Regions' then 0 when AGGREGATION = 'Megaregion' then 1 when AGGREGATION = 'Core Region' then 2 when AGGREGATION = 'Super Region' then 3 when AGGREGATION = 'REGION' then 4 else 5 end,
        case when avg_gross_revenue is null then 1 else 0 end,
        avg_gross_revenue DESC, REGION, WEEK;
      `
      return query
    },

    exportData () {
      return this.dashboard.widgets.filter(widget => widget.metadata.type === 'table')
        .map(widget => ({ name: widget.metadata.title, data: widget.value }))
    },

    megaregionQuery () {
      return `SELECT DISTINCT MEGAREGION FROM ${this.dashboard.dataSource} WHERE
        ${this.dashboard.widgets[3].metadata.name} IN (${createInListString(this.dashboard.widgets[3].value)}) ORDER BY MEGAREGION ASC`
    },

    coreRegionQuery () {
      return `SELECT DISTINCT CORE_REGION FROM ${this.dashboard.dataSource} WHERE
        ${this.dashboard.widgets[4].metadata.name} IN (${createInListString(this.dashboard.widgets[4].value)}) 
        AND ${this.dashboard.widgets[3].metadata.name} IN (${createInListString(this.dashboard.widgets[3].value)}) ORDER BY CORE_REGION ASC`
    },

    superRegionQuery () {
      return `SELECT DISTINCT SUPER_REGION FROM ${this.dashboard.dataSource} WHERE
        ${this.dashboard.widgets[5].metadata.name} IN (${createInListString(this.dashboard.widgets[5].value)}) 
        AND ${this.dashboard.widgets[3].metadata.name} IN (${createInListString(this.dashboard.widgets[3].value)}) ORDER BY SUPER_REGION ASC`
    },

    regionQuery () {
      return `SELECT DISTINCT REGION FROM ${this.dashboard.dataSource} WHERE
        ${this.dashboard.widgets[6].metadata.name} IN (${createInListString(this.dashboard.widgets[6].value)}) 
        AND ${this.dashboard.widgets[3].metadata.name} IN (${createInListString(this.dashboard.widgets[3].value)}) ORDER BY REGION ASC`
    },

    areRouteParamsDefaultFilter () {
      if (_.isEmpty(this.$route.query)) {
        return true
      }

      return (
        (this.$route.query['number_filter'] === '8') &&
        (
          this.$route.query['aggregation'] &&
          this.doArraysMatchUnordered(this.$route.query['aggregation'].split(',').map(aggregation => aggregation.toLowerCase()), DEFAULT_AGGREGATIONS.map(aggregation => aggregation.toLowerCase()))
        ) &&
        (this.$route.query['megaregion'] === 'all-selected') &&
        (this.$route.query['core_region'] === 'all-selected') &&
        (this.$route.query['super_region'] === 'all-selected') &&
        (this.$route.query['region'] === 'all-selected')
      )
    }
  },

  watch: {
    dashboard: {
      deep: true,
      handler (dashboard) {
        if (!this.backfillingRouteQueryParams) {
          const queryParams = dashboard.widgets
            .filter(widget => !['table', 'table-by-value', 'text', 'blank', 'report', 'filter-button'].includes(widget.metadata.type))
            .reduce((acc, curr) => {
              const param = curr.metadata.name.toLowerCase()

              acc[param] = curr.value ? curr.value.toString() : ''

              if (this.isParamForRegionalFilter(param) && this.areAllOptionsSelected(curr.value, curr.options)) {
                acc[param] = 'all-selected'
              }

              return acc
            }, {})
          this.$router.replace({ name: this.$route.name, query: queryParams })
        }
      }
    },

    async megaregionQuery (val) {
      if (!this.initialLoad) {
        try {
          const result = await getData(val, this.queryLimit)
          this.$dashboardQueryLog(this.dashboard.title.name, result.data.queryId, val, 'megaregionQuery')
          if (result.data.error) {
            throw result.data.error
          }
          this.dashboard.widgets[4].options = Object.freeze(result.data.data.filter(x => x[this.dashboard.widgets[4].metadata.name]).map(option => ({
            name: option[this.dashboard.widgets[4].metadata.name],
            value: option[this.dashboard.widgets[4].metadata.name]
          })))
          this.dashboard.widgets[4].value = Object.freeze(result.data.data.map(value => value[this.dashboard.widgets[4].metadata.name]))
        } catch (error) {
          this.$Message.error(error.message)
          console.error(error.message)
        }
      }
    },

    async coreRegionQuery (val) {
      if (!this.initialLoad) {
        try {
          const result = await getData(val, this.queryLimit)
          this.$dashboardQueryLog(this.dashboard.title.name, result.data.queryId, val, 'coreRegionQuery')
          if (result.data.error) {
            throw result.data.error
          }
          this.dashboard.widgets[5].options = Object.freeze(result.data.data.filter(x => x[this.dashboard.widgets[5].metadata.name]).map(option => ({
            name: option[this.dashboard.widgets[5].metadata.name],
            value: option[this.dashboard.widgets[5].metadata.name]
          })))
          this.dashboard.widgets[5].value = Object.freeze(result.data.data.map(value => value[this.dashboard.widgets[5].metadata.name]))
        } catch (error) {
          this.$Message.error(error.message)
          console.error(error.message)
        }
      }
    },

    async superRegionQuery (val) {
      if (!this.initialLoad) {
        try {
          const result = await getData(val, this.queryLimit)
          this.$dashboardQueryLog(this.dashboard.title.name, result.data.queryId, val, 'superRegionQuery')
          if (result.data.error) {
            throw result.data.error
          }
          this.dashboard.widgets[6].options = Object.freeze(result.data.data.filter(x => x[this.dashboard.widgets[6].metadata.name]).map(option => ({
            name: option[this.dashboard.widgets[6].metadata.name],
            value: option[this.dashboard.widgets[6].metadata.name]
          })))
          this.dashboard.widgets[6].value = Object.freeze(result.data.data.map(value => value[this.dashboard.widgets[6].metadata.name]))
        } catch (error) {
          this.$Message.error(error.message)
          console.error(error.message)
        }
      }
    },

    async regionQuery (val) {
      if (!this.initialLoad) {
        try {
          const result = await getData(val, this.queryLimit)
          this.$dashboardQueryLog(this.dashboard.title.name, result.data.data.queryId, val, 'regionQuery')
          if (result.data.error) {
            throw result.data.error
          }
          this.dashboard.widgets[7].options = Object.freeze(result.data.data.filter(x => x[this.dashboard.widgets[7].metadata.name]).map(option => ({
            name: option[this.dashboard.widgets[7].metadata.name],
            value: option[this.dashboard.widgets[7].metadata.name]
          })))
          this.dashboard.widgets[7].value = Object.freeze(result.data.data.map(value => value[this.dashboard.widgets[7].metadata.name]))
        } catch (error) {
          this.$Message.error(error.message)
          console.error(error.message)
        }
      }
    }
  },

  beforeCreate () {
    sessionStorage.setItem('context', 'dashboards')
  },

  // TODO: break created function into smaller functions
  async created () {
    this.dashboardLoadStart = performance.now()

    try {
      axios.get(`${process.env.VUE_APP_API_URI}/dashboards/${this.dashboardId}`)
        .then(response => {
          const dashboards = [...response.data]
          if (dashboards.length) {
            const sharedDashboard = { ...dashboards[0] }
            this.dashboardOwners = sharedDashboard.owners
          }
        })

      this.initialLoad = true

      let useCache = false
      this.dashboard.widgets[0].loading = true

      // TODO: Initializing and backfilling filters is quite complicated and difficult to understand.
      // When redesigning filter on UE dashboard try to simplify the process.
      if (this.areRouteParamsDefaultFilter) {
        this.initializeDefaultFilters()

        // TODO: find way to generalize backfilling. Right now there seems to be too many edge cases to keep
        // track of
        if (this.$route.query['select_view']) {
          this.dashboard.widgets[1].value = this.$route.query['select_view']
          this.handleSelectionChange(1, this.$route.query['select_view'])
        }

        useCache = true
      } else {
        this.backfillingRouteQueryParams = true
        await this.initializeRegionOptions(this.$route.query['aggregation'] ? this.$route.query['aggregation'].split(',') : [])
        // backfilling filters from URL params
        Object.keys(this.$route.query).map(param => {
          this.dashboard.widgets.forEach(filter => {
            if (filter.metadata.name && filter.metadata.name.toLowerCase() === param) {
              if (this.isParamForRegionalFilter(param) && this.$route.query[param] === 'all-selected') {
                filter.value = filter.options.map(option => option.value)
              } else {
                filter.value = this.$route.query[param]
              }
            }
          })

          // Handle initial table show/hide
          if (param === 'select_view') {
            this.handleSelectionChange(1, this.$route.query[param])
          }
        })

        // Make sure checkbox/select-multiple types are array not string
        this.dashboard.widgets.forEach(filter => {
          if (['checkbox', 'select-multiple'].includes(filter.metadata.type) && typeof filter.value === 'string') {
            filter.value = filter.value.split(',')
          }
        })

        this.backfillingRouteQueryParams = false
      }

      await this.loadUnitEconomicsData(useCache)

      this.dashboard.widgets[0].loading = false
      this.initialLoad = false
      this.initialDataLoaded = true
    } catch (error) {
      console.error(`Error on initial loading of UE dashboard: ${error.message}`)
    }
  },

  updated () {
    if (this.initialDataLoaded) {
      if (!this.dashboardOpenLogged) {
        this.$nextTick(() => {
          this.dashboardLoadEnd = performance.now()
          this.$customDashboardOpenLog(this.dashboard.title.name, this.dashboardLoadEnd - this.dashboardLoadStart)
          this.dashboardOpenLogged = true
        })
      }
    }
  },

  methods: {
    async loadUnitEconomicsData (useCache = false) {
      try {
        this.$set(this.dashboard.widgets[0], 'loading', true)

        const result = await (useCache ? this.getCachedData() : getData(this.query, this.queryLimit))

        if (result.data.error) {
          throw new Error(result.data.error.message)
        }

        this.$dashboardQueryLog(this.dashboard.title.name, useCache ? 'cached' : result.data.queryId, useCache ? 'cached' : this.query, 'ueQuery')
        this.$set(this.dashboard.widgets[0], 'value', Object.freeze(result.data.data))
        this.setContextData()
        this.$set(this.dashboard.widgets[0], 'loading', false)
      } catch (error) {
        console.error(`Error while loading unit economics data: ${error.message}`)
        this.$Message.error({
          content: 'Error while loading Unit Economics Dashboard',
          duration: 5
        })
      }
    },

    /**
     * Handles table show/hide based on user's selection
     * @param {Number} i index in widget
     * @param {String} val value of Expanded/Simple
     */
    handleSelectionChange (i, val) {
      if (i === 1) {
        if (val === 'Expanded') {
          this.dashboard.widgets[0].columns = EXPANDED_VIEW_COLUMNS
          this.dashboard.widgets[0].h = EXPANDED_VIEW_HEIGHT
        } else if (val === 'Simple') {
          this.dashboard.widgets[0].columns = SIMPLE_VIEW_COLUMNS
          this.dashboard.widgets[0].h = SIMPLE_VIEW_HEIGHT
        }
      }
    },

    async initializeRegionOptions (aggregations) {
      try {
        const result = await getData(`
          SELECT DISTINCT AGGREGATION, MEGAREGION, CORE_REGION, SUPER_REGION, REGION
          FROM ${this.dashboard.dataSource}
          WHERE AGGREGATION IN (${aggregations.map(aggregation => `'${aggregation}'`).join(', ')})
          ORDER BY MEGAREGION, CORE_REGION, SUPER_REGION, REGION
        `)

        if (result.data.error) {
          throw new Error(result.data.error.message)
        }

        const megaregionSet = new Set()
        const coreRegionSet = new Set()
        const superRegionSet = new Set()
        const regionSet = new Set()

        result.data.data.forEach(row => {
          megaregionSet.add(row['MEGAREGION'])
          coreRegionSet.add(row['CORE_REGION'])
          superRegionSet.add(row['SUPER_REGION'])
          regionSet.add(row['REGION'])
        })

        this.dashboard.widgets[4].options = Array.from(megaregionSet).filter(regionName => regionName).map(regionName => ({ name: regionName, value: regionName }))
        this.dashboard.widgets[5].options = Array.from(coreRegionSet).filter(regionName => regionName).map(regionName => ({ name: regionName, value: regionName }))
        this.dashboard.widgets[6].options = Array.from(superRegionSet).filter(regionName => regionName).map(regionName => ({ name: regionName, value: regionName }))
        this.dashboard.widgets[7].options = Array.from(regionSet).filter(regionName => regionName).map(regionName => ({ name: regionName, value: regionName }))
      } catch (error) {
        console.error(`Error while initializing region filters: ${error.message}`)
      }
    },

    async initializeDefaultFilters () {
      try {
        this.dashboard.widgets[3].value = [...DEFAULT_AGGREGATIONS]
        await this.initializeRegionOptions(DEFAULT_AGGREGATIONS)
        this.dashboard.widgets[4].value = this.dashboard.widgets[4].options.map(option => option.value)
        this.dashboard.widgets[5].value = this.dashboard.widgets[5].options.map(option => option.value)
        this.dashboard.widgets[6].value = this.dashboard.widgets[6].options.map(option => option.value)
        this.dashboard.widgets[7].value = this.dashboard.widgets[7].options.map(option => option.value)
      } catch (error) {
        console.error(`Error while initializing default filters: ${error.message}`)
        throw error
      }
    },

    async getCachedData () {
      try {
        const result = await axios.get(`${process.env.VUE_APP_API_URI}/custom-dashboards/unit-economics`)

        return result
      } catch (error) {
        console.error(`Error while getting cached data for UE dashboard: ${error.message}`)
        throw error
      }
    },

    areAllOptionsSelected (values, options) {
      return this.doArraysMatchUnordered(values, options.map(option => option.value))
    },

    doArraysMatchUnordered (arrayA, arrayB) {
      const arrayASorted = [...arrayA].sort()
      const arrayBSorted = [...arrayB].sort()

      return arrayASorted.length === arrayBSorted.length && arrayASorted.every((value, index) => value === arrayBSorted[index])
    },

    isParamForRegionalFilter (param) {
      return ['megaregion', 'core_region', 'super_region', 'region'].includes(param)
    },

    createContextHolidayArr (holidays) {
      return [...holidays].sort((holidayA, holidayB) => new Date(holidayA.date) - new Date(holidayB.date))
        .map(holiday => `${holiday.name} (${this.$moment(holiday.date).format('ddd')})`)
    },

    createRegionToDateToHolidayMap (holidayData, regionIdToNameMap) {
      return holidayData.reduce((acc, holiday) => {
        const calendarDate = holiday.CALENDAR_DATE
        const daysToAdd = holiday.DAY_OF_WEEK - 1
        const holidayCalendarDate = this.$moment(calendarDate).add(daysToAdd, 'days').format('YYYY-MM-DD')
        const regionName = regionIdToNameMap[holiday.REGION_ID]

        if (!_.has(acc, [regionName, calendarDate])) {
          _.set(acc, [regionName, calendarDate], [])
        }

        acc[regionName][calendarDate].push({
          date: holidayCalendarDate,
          name: holiday.HOLIDAY
        })

        return acc
      }, {})
    },

    setContextTitles (contextData) {
      Object.keys(contextData).forEach(regionName => {
        Object.keys(contextData[regionName]).forEach(calendarDate => {
          const context = contextData[regionName][calendarDate]
          const weatherChartData = context.WEATHER_CHART
          const avgMaxTemp = weatherChartData.reduce((acc, weatherChartPoint) => acc + weatherChartPoint.MAX_TEMP, 0) / weatherChartData.length
          const avgMinTemp = weatherChartData.reduce((acc, weatherChartPoint) => acc + weatherChartPoint.MIN_TEMP, 0) / weatherChartData.length
          const avgPrecipitation = weatherChartData.reduce((acc, weatherChartPoint) => acc + weatherChartPoint.PRECIPITATION, 0) / weatherChartData.length

          context.TEMPERATURE_TITLE = `Avg Max (${avgMaxTemp.toFixed(2)}); Avg Min (${avgMinTemp.toFixed(2)})`
          context.PRECIPITATION_TITLE = `Avg (${avgPrecipitation.toFixed(2)})`
        })
      })
    },

    sortContextWeatherChartData (contextData) {
      Object.keys(contextData).forEach(regionName => {
        Object.keys(contextData[regionName]).forEach(calendarDate => {
          const context = contextData[regionName][calendarDate]

          if (context.WEATHER_CHART) {
            context.WEATHER_CHART.sort((a, b) => new Date(a.CALENDAR_DATE) - new Date(b.CALENDAR_DATE))
          }
        })
      })
    },

    addContextWeatherData (contextData, weatherData, regionIdToNameMap) {
      const contextDataCopy = _.cloneDeep(contextData)

      weatherData.forEach(row => {
        const calendarDate = row.CALENDAR_DATE
        const dayOfWeek = row.PART_OF_PERIOD
        const daysToAdd = dayOfWeek - 1 >= 0 ? dayOfWeek - 1 : dayOfWeek - 1 + 7 // Sundays are mapped to 0 so we need to add a whole week
        const chartPointCalendarDate = this.$moment(calendarDate).add(daysToAdd, 'days').format('YYYY-MM-DD')
        const temperature = row.TEMPERATURE
        const tempType = row.TEMP_TYPE.toLowerCase()
        const precipitation = row.PRECIPITATION
        const regionId = row.REGION_ID
        const regionName = regionIdToNameMap[regionId]

        if (!_.has(contextDataCopy, [regionName, calendarDate])) {
          _.set(contextDataCopy, [regionName, calendarDate], {
            WEATHER_CHART: [],
            HOLIDAYS: []
          })
        }

        const contextObj = contextDataCopy[regionName][calendarDate]
        let chartDataPoint = contextObj.WEATHER_CHART.find(dataPoint => dataPoint.CALENDAR_DATE === chartPointCalendarDate)

        if (!chartDataPoint) {
          chartDataPoint = {
            CALENDAR_DATE: chartPointCalendarDate,
            MAX_TEMP: null,
            MIN_TEMP: null,
            PRECIPITATION: precipitation
          }

          contextObj.WEATHER_CHART.push(chartDataPoint)
        }

        if (tempType === 'maximum') {
          chartDataPoint.MAX_TEMP = temperature
        } else if (tempType === 'minimum') {
          chartDataPoint.MIN_TEMP = temperature
        }
      })

      return contextDataCopy
    },

    addContextHolidayData (contextData, holidayData, regionIdToNameMap) {
      const contextDataCopy = _.cloneDeep(contextData)

      const regionToDateToHolidayMap = this.createRegionToDateToHolidayMap(holidayData, regionIdToNameMap)

      Object.keys(contextDataCopy).forEach(regionName => {
        Object.keys(contextDataCopy[regionName]).forEach(calendarDate => {
          const contextObj = contextDataCopy[regionName][calendarDate]
          const holidays = _.get(regionToDateToHolidayMap, [regionName, calendarDate])

          if (holidays) {
            contextObj.HOLIDAYS = this.createContextHolidayArr(holidays)
          }
        })
      })

      return contextDataCopy
    },

    createContextData (weatherData, holidayData, regionIdToNameMap) {
      let contextData = {}

      contextData = this.addContextWeatherData(contextData, weatherData, regionIdToNameMap)
      contextData = this.addContextHolidayData(contextData, holidayData, regionIdToNameMap)

      this.setContextTitles(contextData)
      this.sortContextWeatherChartData(contextData)

      return contextData
    },

    async setContextData () {
      try {
        this.dashboard.widgets[0].headerColumn.contextData = {}
        let startDate = ''
        let endDate = ''
        const regionIdToNameMap = {}

        this.dashboard.widgets[0].value.forEach(row => {
          if (!startDate) {
            startDate = row.WEEK
          } else {
            startDate = new Date(row.WEEK) < new Date(startDate) ? row.WEEK : startDate
          }

          if (!endDate) {
            endDate = row.WEEK
          } else {
            endDate = new Date(row.WEEK) > new Date(endDate) ? row.WEEK : endDate
          }

          if (row.REGION_ID && !regionIdToNameMap[row.REGION_ID]) {
            regionIdToNameMap[row.REGION_ID] = row.REGION
          }
        })

        const regionIds = Object.keys(regionIdToNameMap)

        if (startDate && endDate && regionIds.length) {
          const weatherQuery = createWeatherQuery(startDate, endDate, regionIds, 'Weekly')
          const holidayQuery = createHolidayQuery(startDate, endDate, regionIds, 'Weekly')
          const [weatherResult, holidayResult] = await Promise.all([
            getData(weatherQuery, this.queryLimit),
            getData(holidayQuery, this.queryLimit)
          ])

          if (weatherResult.data.error) {
            throw new Error(`Error while getting weather data: ${weatherResult.data.error.message}`)
          }

          if (holidayResult.data.error) {
            throw new Error(`Error while getting holiday data: ${holidayResult.data.error.message}`)
          }

          this.dashboard.widgets[0].headerColumn.contextData = Object.freeze(this.createContextData(weatherResult.data.data, holidayResult.data.data, regionIdToNameMap))
        }
      } catch (error) {
        console.error(`Error while setting context data for UE dashboard: ${error.message}`)
      }
    }
  }
}

const opacityFuncFinance = function (rowObj) {
  if (rowObj.RECENT_2_WEEKS) {
    return 0.5
  } else {
    return 1
  }
}

const opacityFuncHomebase = function (rowObj) {
  if (rowObj.RECENT_2_WEEKS) {
    return 1
  } else {
    return 0.5
  }
}

const EXPANDED_VIEW_COLUMNS = {
  HEAD_TITLE_1: {
    displayName: 'Market Metrics',
    styles: {
      fontWeight: 'bold',
      textDecoration: 'underline'
    }
  },
  GROSS_REVENUE: {
    displayName: 'Gross Bookings',
    format: 'number',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'WOW_GROSS_REVENUE',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(229, 238, 240)'
    }
  },
  TRIPS: {
    displayName: 'Trips',
    format: 'number',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'WOW_TRIPS',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(229, 238, 240)'
    },
    isExpandedView: true
  },
  OPERATIONAL_VEHICLES: {
    displayName: 'Operational Vehicles',
    format: 'number',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'WOW_OPERATIONAL_VEHICLES',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(229, 238, 240)'
    }
  },
  RVD: {
    displayName: 'RVD',
    format: 'decimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'WOW_RVD',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(229, 238, 240)'
    }
  },
  TVD: {
    displayName: 'TVD',
    format: 'decimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'WOW_TVD',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(229, 238, 240)'
    }
  },
  RPT: {
    displayName: 'RPT',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'WOW_RPT',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(229, 238, 240)'
    }
  },
  CONTRIBUTION_MARGIN: {
    displayName: 'Contribution Margin % GB *',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(246, 232, 238)',
      fontWeight: 'bold',
      fontStyle: 'italic'
    }
  },
  GROSS_MARGIN: {
    displayName: 'City Profit % GB *',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(246, 232, 238)',
      fontWeight: 'bold',
      fontStyle: 'italic'
    }
  },
  CITY_PROFIT_PER_VEHICLE_PER_DAY: {
    displayName: 'City Profit Per Vehicle Per Day *',
    format: 'currencyDecimal',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(246, 232, 238)',
      fontWeight: 'bold',
      fontStyle: 'italic'
    },
    isExpandedView: true
  },
  CITY_EBITDA_PCT_GROSS: {
    displayName: 'City EBITDA % GB *',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(246, 232, 238)',
      fontWeight: 'bold',
      fontStyle: 'italic'
    }
  },
  CITY_EBITDA_PER_VEHICLE_PER_DAY: {
    displayName: 'City EBITDA Per Vehicle Per Day *',
    format: 'currencyDecimal',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    background: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [-0.05, 0.05],
      colorRange: ['F1BFC1', 'C5DABF']
    },
    styles: {
      backgroundColor: 'rgb(246, 232, 238)',
      fontWeight: 'bold',
      fontStyle: 'italic'
    }
  },
  LOCAL_OPS_PAYOUT_SUBMITTED: {
    displayName: 'Local Ops Payout Submitted',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0.8, 0.8],
      colorRange: ['FF0000', '5A516E']
    },
    styles: {
      fontStyle: 'italic'
    },
    isExpandedView: true
  },
  HOMEBASE_SCHEDULE_COMPLIANCE: {
    displayName: 'Homebase Schedule Compliance',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'HOMEBASE_SCHEDULE_COMPLIANCE',
      displayName: 'Homebase Schedule Compliance',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'HOMEBASE_SCHEDULE_COMPLIANCE',
      valueRange: [0.8, 0.8],
      colorRange: ['FF0000', '5A516E']
    },
    styles: {
      fontStyle: 'italic'
    },
    isExpandedView: true
  },
  HOMEBASE_FINANCE_COMPLIANCE: {
    displayName: 'Homebase Finance Compliance',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'HOMEBASE_FINANCE_COMPLIANCE',
      displayName: 'Homebase Finance Compliance',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'HOMEBASE_FINANCE_COMPLIANCE',
      valueRange: [0.8, 0.8],
      colorRange: ['FF0000', '5A516E']
    },
    styles: {
      fontStyle: 'italic'
    },
    isExpandedView: true
  },
  DIVIDER_1: {
    displayName: ' '
  },
  HEAD_TITLE_2: {
    displayName: 'Weekly Unit Economics',
    styles: {
      fontWeight: 'bold',
      textDecoration: 'underline'
    }
  },
  GROSS_BOOKINGS_REF: {
    displayName: 'Gross Bookings',
    refColumn: 'GROSS_REVENUE',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(217, 234, 211)'
    }
  },
  DIVIDER_2: {
    displayName: ' '
  },
  DISCOUNTS_PROMOTIONS_UNCOLLECTED_TAX: {
    displayName: 'Discounts/Promotions/Uncollected Revenue/Tax',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(243, 243, 243)'
    }
  },
  DISCOUNTS_PROMOTIONS_UNCOLLECTED_TAX_PCT_GROSS: {
    displayName: 'Discounts/Promotions/Uncollected Revenue/Tax % GB',
    value: 1,
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  DISCOUNTS_PROMOTIONS: {
    displayName: '       Discounts Promotions',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    isExpandedView: true
  },
  DISCOUNTS_PROMOTIONS_PCT_GROSS: {
    displayName: '       Discounts Promotions % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  REFUNDS: {
    displayName: '       Refunds',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    isExpandedView: true
  },
  REFUND_PCT_GROSS: {
    displayName: '       Refunds % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  UNCOLLECTED_REVENUE: {
    displayName: '       Uncollected Revenue',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    isExpandedView: true
  },
  UNCOLLECTED_REVENUE_PCT_GROSS: {
    displayName: '       Uncollected Revenue % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  SALES_TAX: {
    displayName: '       Sales Tax',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    isExpandedView: true
  },
  SALES_TAX_PCT_GROSS: {
    displayName: '       Sales Tax % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  SALES_TAX_PCT_NET_REVENUE: {
    displayName: '       Sales Tax % Net Revenue',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  DIVIDER_3: {
    displayName: ' '
  },
  NET_REVENUE: {
    displayName: 'Net Revenue',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(217, 234, 211)'
    }
  },
  NET_REVENUE_PCT_GROSS: {
    displayName: 'Net Revenue % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  DIVIDER_4: {
    displayName: ' '
  },
  PAYMENT_INSURANCE_NETWORK_FEES: {
    displayName: 'Payment, Insurance, Network Fees *',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(243, 243, 243)'
    }
  },
  PAYMENT_INSURANCE_NETWORK_FEES_PCT_GROSS: {
    displayName: 'Payment, Insurance, Network Fees % GB *',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  PAYMENT_FEES: {
    displayName: '       Payment',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    isExpandedView: true
  },
  PAYMENT_FEES_PCT_GROSS: {
    displayName: '       Payment % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  INSURANCE_FEES: {
    displayName: '       Insurance',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    isExpandedView: true
  },
  INSURANCE_FEES_PCT_GROSS: {
    displayName: '       Insurance % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  NETWORK_FEES: {
    displayName: '       Network Fees',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    isExpandedView: true
  },
  NETWORK_FEES_PCT_GROSS: {
    displayName: '       Network Fees % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  NET_DEPRECIATION: {
    displayName: 'Net Depreciation',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(243, 243, 243)'
    }
  },
  NET_DEPRECIATION_PCT_GROSS: {
    displayName: 'Net Depreciation % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  LABOR_COST: {
    displayName: 'Labor (Juicer & Local Ops) *',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(243, 243, 243)'
    }
  },
  LABOR_COST_PCT_GROSS: {
    displayName: 'Labor (Juicer & Local Ops) % GB *',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  JUICER_PAYOUT: {
    displayName: '       Juicer Payout',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    }
  },
  JUICER_PAYOUT_PCT_GROSS: {
    displayName: '       Juicer Payout % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  JUICER_PAYMENT_FEES: {
    displayName: '       Juicer Payment Fee',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    }
  },
  JUICER_PAYMENT_FEES_PCT_GROSS: {
    displayName: '       Juicer Payment Fee % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  FINANCE_LOCAL_OPS_PAYOUT: {
    displayName: '       Finance Local Ops Payout',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    opacity: {
      func: opacityFuncFinance
    }
  },
  FINANCE_LOCAL_OPS_PAYOUT_PCT_GROSS: {
    displayName: '       Finance Local Ops Payout % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    opacity: {
      func: opacityFuncFinance
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  HOMEBASE_LOCAL_OPS_PAYOUT: {
    displayName: '       Homebase Local Ops Payout',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    opacity: {
      func: opacityFuncHomebase
    }
  },
  HOMEBASE_LOCAL_OPS_PAYOUT_PCT_GROSS: {
    displayName: '       Homebase Local Ops Payout % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    opacity: {
      func: opacityFuncHomebase
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  LOCAL_OPS_OTHERS: {
    displayName: 'Local Ops Others *',
    format: 'currencyDecimal',
    info: 'Tools/Parts, Vehicles, Permits, Fines & Penalties, Other',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(243, 243, 243)'
    }
  },
  LOCAL_OPS_OTHERS_PCT_GROSS: {
    displayName: 'Local Ops Others % GB *',
    format: 'percentage',
    info: 'Tools/Parts, Vehicles, Permits, Fines & Penalties, Other',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  TOOLS_PARTS: {
    displayName: '       Tools/Parts',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    }
  },
  TOOLS_PARTS_PCT_GROSS: {
    displayName: '       Tools/Parts % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  VEHICLES: {
    displayName: '       Vehicles (Vans, Gas & Accidents)',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    isExpandedView: true
  },
  VEHICLES_PCT_GROSS: {
    displayName: '       Vehicles (Vans, Gas & Accidents) % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  OPS_FINES_AND_PENALTIES: {
    displayName: '       Permits, Fines & Penalties',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    isExpandedView: true
  },
  OPS_FINES_AND_PENALTIES_PCT_GROSS: {
    displayName: '       Permits, Fines & Penalties % GB',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  OPS_OTHERS: {
    displayName: '       Ops Others',
    info: 'G&A, Professional Services, Technology, Marketing',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    isExpandedView: true
  },
  OPS_OTHERS_PCT_GROSS: {
    displayName: '       Ops Others % GB',
    info: 'G&A, Professional Services, Technology, Marketing',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    },
    isExpandedView: true
  },
  CSR: {
    displayName: 'CX *',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(243, 243, 243)'
    }
  },
  CSR_PCT_GROSS: {
    displayName: 'CX % GB *',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  DIVIDER_5: {
    displayName: ' '
  },
  CONTRIBUTION_PROFIT: {
    displayName: 'Contribution Margin *',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontWeight: 'bold',
      backgroundColor: 'rgb(217, 234, 211)'
    }
  },
  CONTRIBUTION_MARGIN_1: {
    displayName: 'Contribution Margin % GB *',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontWeight: 'bold',
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  DIVIDER_6: {
    displayName: ' '
  },
  LOCAL_OPS_FIXED_COST: {
    displayName: 'Local Ops Fixed Costs (FTE, T&E, WH) *',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    }
  },
  LOCAL_OPS_FIXED_COST_PCT_GROSS: {
    displayName: 'Local Ops Fixed Costs (FTE, T&E, WH) % GB *',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  DIVIDER_7: {
    displayName: ' '
  },
  LOGISTICS: {
    displayName: 'Logistics *',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    }
  },
  LOGISTICS_PCT_GROSS: {
    displayName: 'Logistics % GB *',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  DIVIDER_8: {
    displayName: ' '
  },
  CONTRIBUTION_PROFIT_LESS_FIXED_COSTS: {
    displayName: 'City Profit *',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontWeight: 'bold',
      backgroundColor: 'rgb(217, 234, 211)'
    }
  },
  GROSS_MARGIN_1: {
    displayName: 'City Profit % GB *',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontWeight: 'bold',
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  DIVIDER_9: {
    displayName: ' '
  },
  CITY_EBITDA: {
    displayName: 'City EBITDA *',
    format: 'currencyDecimal',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontWeight: 'bold',
      backgroundColor: 'rgb(217, 234, 211)'
    }
  },
  CITY_EBITDA_PCT_GROSS_REF: {
    displayName: 'City EBITDA % GB *',
    refColumn: 'CITY_EBITDA_PCT_GROSS',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      fontWeight: 'bold',
      fontStyle: 'italic',
      color: 'rgb(153, 153, 153)'
    }
  },
  DIVIDER_10: {
    displayName: ' '
  },
  HEAD_TITLE_3: {
    displayName: 'Net Decay Metrics',
    styles: {
      fontWeight: 'bold',
      textDecoration: 'underline',
      backgroundColor: 'rgb(255, 242, 204)'
    }
  },
  NET_DECAY_RATE: {
    displayName: 'Net Decay Rate',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(255, 242, 204)'
    }
  },
  DECAY_RATE: {
    displayName: '       Decay Rate',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(255, 242, 204)'
    }
  },
  REVIVAL_RATE: {
    displayName: '       Revival Rate',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(255, 242, 204)'
    }
  },
  NET_DECAYED_VEHICLES: {
    displayName: 'Net Decayed Vehicles',
    format: 'number',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(255, 242, 204)'
    }
  },
  DECAYED_VEHICLES: {
    displayName: '       Decayed Vehicles',
    format: 'number',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(255, 242, 204)'
    }
  },
  REVIVED_VEHICLES: {
    displayName: '       Revived Vehicles',
    format: 'number',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(255, 242, 204)'
    }
  },
  SICK_RATE: {
    displayName: 'Sick Rate',
    format: 'percentage',
    precision: 1,
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(255, 242, 204)'
    }
  },
  FULLY_LOADED_VEHICLE_COST: {
    displayName: 'Fully Loaded Vehicle Cost',
    format: 'currency',
    tooltip: [{
      column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      displayName: 'Local Ops Payout Submitted',
      format: 'percentage',
      precision: 1
    }],
    color: {
      comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      valueRange: [0, 1],
      colorRange: ['804002', '515A6E']
    },
    styles: {
      backgroundColor: 'rgb(255, 242, 204)'
    }
  }
}

const SIMPLE_VIEW_COLUMNS = Object.keys(EXPANDED_VIEW_COLUMNS).reduce((acc, key) => {
  const column = EXPANDED_VIEW_COLUMNS[key]

  if (!column.isExpandedView) {
    acc[key] = column
  }

  return acc
}, {})

/**
 * Explanation of height calculation:
 * The height of the dashboard view is calculated as so: Height in pixels = h * (rowHeight + 10) - 10
 * SIMPLE_VIEW_HEIGHT and EXPANDED_VIEW_HEIGHT represent the h value in the above equation. Since the
 * pixel height of each row in the dashboard is 30px the term "Height in pixels" is equal to
 * (Number of columns + 2) * 30. The plus 2 represents the additional header columns in the dashboard.
 * The term "rowHeight" is equivalent to 10 because that is the default value for the height of a row
 * given by the vue-grid library. Solving for h, we get the following equation:
 * h = ((NUMBER_COLUMNS + 2) * 30 + 10) / 20
 */
const SIMPLE_VIEW_HEIGHT = ((Object.keys(SIMPLE_VIEW_COLUMNS).length + 2) * 30 + 10) / 20
const EXPANDED_VIEW_HEIGHT = ((Object.keys(EXPANDED_VIEW_COLUMNS).length + 2) * 30 + 10) / 20

const DASHBOARD = {
  dashboardName: 'Local Ops Dashboard Overview',
  title: {
    name: 'Weekly Unit Economics -- Best Estimate',
    isShown: false
  },
  dataSource: 'PRODUCTION_DATA.DM.NEW_UE_REPORT_WEEKLY_VIEW',
  widgets: [{
    x: 0,
    y: 1,
    w: 20,
    h: SIMPLE_VIEW_HEIGHT,
    i: 0,
    loading: false,
    metadata: {
      name: 'weeklyUnitEconomicsTable',
      title: 'Weekly Unit Economics Table',
      type: 'table',
      customField: false,
      tableType: 'horizontal',
      groupBy: 'REGION'
    },
    styles: {
      leftColWidth: 310
    },
    headerColumn: {
      name: 'WEEK',
      displayName: 'Day of Week',
      format: 'date',
      fromFormat: 'YYYY-MM-DD',
      toFormat: 'MM/DD (ddd)',
      contextTooltipDisplayName: 'Week of',
      contextTooltip: [{
        displayName: 'Temperature (\xB0C)',
        key: 'TEMPERATURE_TITLE'
      }, {
        key: 'WEATHER_CHART',
        chart: {
          dataTraces: [
            {
              x: 'CALENDAR_DATE',
              y: 'MAX_TEMP',
              color: 'red',
              name: 'Max'
            },
            {
              x: 'CALENDAR_DATE',
              y: 'MIN_TEMP',
              color: 'blue',
              name: 'Min'
            }
          ],
          xAxis: {
            tickFormat: '%a',
            tickStep: 1000 * 60 * 60 * 24 // increment by 1 day
          }
        }
      }, {
        displayName: 'Precipitation (mm)',
        key: 'PRECIPITATION_TITLE'
      }, {
        key: 'WEATHER_CHART',
        chart: {
          dataTraces: [
            {
              x: 'CALENDAR_DATE',
              y: 'PRECIPITATION',
              color: 'blue'
            }
          ],
          xAxis: {
            tickFormat: '%a',
            tickStep: 1000 * 60 * 60 * 24
          },
          yAxis: {
            nonNegative: true
          }
        }
      }, {
        displayName: 'Holiday(s)',
        key: 'HOLIDAYS',
        format: 'list-string',
        delimiter: ', ',
        emptyDefault: 'None'
      }],
      contextData: {}
    },
    additionalHeaderColumn: {
      name: 'REGION',
      displayName: 'Region',
      format: 'string'
    },
    columns: SIMPLE_VIEW_COLUMNS,
    value: []
  }, {
    x: 20,
    y: 0,
    w: 4,
    h: 6,
    i: 1,
    metadata: {
      name: 'SELECT_VIEW',
      title: 'Select a View',
      type: 'select',
      customField: true
    },
    value: 'Simple',
    options: [{ name: 'Simple', value: 'Simple' }, { name: 'Expanded', value: 'Expanded' }]
  }, {
    x: 20,
    y: 6,
    w: 4,
    h: 6,
    i: 2,
    metadata: {
      name: 'NUMBER_FILTER',
      title: '# of Weeks',
      type: 'input-number',
      customField: true
    },
    value: 8
  }, {
    x: 20,
    y: 12,
    w: 4,
    h: 6,
    i: 3,
    metadata: {
      name: 'AGGREGATION',
      title: 'Aggregation',
      type: 'select-multiple',
      customField: true
    },
    value: [...DEFAULT_AGGREGATIONS],
    options: [{
      name: 'All Regions',
      value: 'All Regions'
    }, {
      name: 'Megaregion',
      value: 'Megaregion'
    }, {
      name: 'Core Region',
      value: 'Core Region'
    }, {
      name: 'Super Region',
      value: 'Super Region'
    }, {
      name: 'Region',
      value: 'Region'
    }]
  }, {
    x: 20,
    y: 18,
    w: 4,
    h: 6,
    i: 4,
    metadata: {
      name: 'MEGAREGION',
      title: 'Megaregion',
      type: 'select-multiple',
      customField: false
    },
    query: 'SELECT DISTINCT MEGAREGION FROM PRODUCTION_DATA.DM.NEW_UE_REPORT_WEEKLY_VIEW ORDER BY MEGAREGION ASC',
    value: [],
    options: []
  }, {
    x: 20,
    y: 24,
    w: 4,
    h: 6,
    i: 5,
    metadata: {
      name: 'CORE_REGION',
      title: 'Core Region',
      type: 'select-multiple',
      customField: false
    },
    query: 'SELECT DISTINCT CORE_REGION FROM PRODUCTION_DATA.DM.NEW_UE_REPORT_WEEKLY_VIEW ORDER BY CORE_REGION ASC',
    value: [],
    options: []
  }, {
    x: 20,
    y: 30,
    w: 4,
    h: 6,
    i: 6,
    metadata: {
      name: 'SUPER_REGION',
      title: 'Super Region',
      type: 'select-multiple',
      customField: false
    },
    query: 'SELECT DISTINCT SUPER_REGION FROM PRODUCTION_DATA.DM.NEW_UE_REPORT_WEEKLY_VIEW WHERE AGGREGATION IN (\'All Regions\', \'Megaregion\', \'Core Region\') ORDER BY SUPER_REGION ASC',
    value: [],
    options: []
  }, {
    x: 20,
    y: 36,
    w: 4,
    h: 8,
    i: 7,
    metadata: {
      name: 'REGION',
      title: 'Region',
      type: 'select-multiple',
      customField: false
    },
    query: 'SELECT DISTINCT REGION FROM PRODUCTION_DATA.DM.NEW_UE_REPORT_WEEKLY_VIEW WHERE SUPER_REGION IN (\'All Regions\', \'Megaregion\') AND AGGREGATION IN (\'All Regions\', \'Megaregion\', \'Core Region\') ORDER BY REGION ASC',
    value: [],
    options: []
  }, {
    x: 20,
    y: 44,
    w: 4,
    h: 4,
    i: 8,
    metadata: {
      type: 'filter-button',
      customField: true
    }
  }, {
    x: 20,
    y: 48,
    w: 4,
    h: 4,
    i: 9,
    metadata: {
      type: 'text',
      customField: true
    },
    styles: {
      fontWeight: 'bold'
    },
    value: 'Lime-S; fixed costs based on last month\'s spend'
  }, {
    x: 20,
    y: 52,
    w: 4,
    h: 4,
    i: 10,
    metadata: {
      type: 'text',
      customField: true
    },
    styles: {
      fontWeight: 'bold',
      backgroundColor: 'rgb(239, 235, 232)'
    },
    value: '* Indicates Finance Estimation is used to calculate metric'
  }, {
    x: 20,
    y: 56,
    w: 4,
    h: 5,
    i: 11,
    metadata: {
      type: 'text',
      customField: true
    },
    value: 'Hover over calendar dates to view contextual (weather/holiday) data. Only works for <b>"Region" geo aggregations</b>.'
  }, {
    x: 20,
    y: 61,
    w: 4,
    h: 4,
    i: 12,
    metadata: {
      type: 'report',
      customField: true
    }
  }]
}
</script>
